export const tripsEn = {
  pageTitle: 'My Trips',
  showPast: 'All',
  hidePast: 'Upcoming',
  showPastLabel: 'Show past trips',
  filteredOutPast: 'No upcoming trips. Toggle to show past trips.',
  addTrip: 'Add trip',
  noTrips: 'No trips yet.',
  noTripsDescription: 'Create your first trip to start planning day by day.',
  untitledTrip: 'Untitled trip',
  cityCount: (n: number) => (n === 1 ? '1 city' : `${n} cities`),
  countdown: {
    title: 'Countdown to your trip',
    years: 'Years',
    months: 'Months',
    days: 'Days',
    hours: 'Hours',
    minutes: 'Minutes',
  },
  columns: {
    title: 'Trip',
    type: 'Type',
    dates: 'Dates',
    cities: 'Cities',
  },
  tripDates: (start: string | null, end: string | null): string => {
    if (!start && !end) return '—';
    if (start && end) return `${start} – ${end}`;
    return start || end || '—';
  },
} as const;

export const tripDetailEn = {
  back: 'Back to My Trips',
  untitled: 'Untitled trip',
  planItinerary: 'Plan itinerary',
  editTrip: 'Edit Trip',
  hotels: 'Hotels',
  planning: 'Planning',
  checklistTitle: 'Trip Checklist',
  checklistExpand: 'Expand checklist',
  checklistCollapse: 'Collapse checklist',
  checklistEmpty: 'No checklist items yet. Add tasks to prepare for this trip.',
  checklistMore: 'Checklist options',
  addTask: 'Add task',
  taskTitlePlaceholder: 'Checklist item',
  deleteTodoItems: 'Delete To Do Items',
  noTasksToDelete: 'There are no items to delete.',
  removeTask: (title: string) => `Remove ${title}`,
  docked: 'Docked',
  tendered: 'Tendered',
  cruising: 'Cruising',
  aiPlan: 'AI Plan',
  noDays: 'No days planned yet',
  noDaysDescription: 'This trip doesn’t have any days yet. Add cities or import a cruise itinerary.',
  openDay: 'Open day plan',
  countryInfo: 'Country information',
  deleteTrip: 'Delete trip',
  deleteConfirm: 'Delete this trip?',
  save: 'Save',
  cancel: 'Cancel',
  done: 'Done',
  sectionDetails: 'Trip details',
  sectionCities: 'Cities',
  sectionOptions: 'City options',
  title: 'Title',
  tripType: 'Trip type',
  dates: 'Dates',
  info: 'Notes',
  addCity: 'Add city',
  cityName: 'City name',
  cityPlaceholder: 'e.g. Paris',
  noCities: 'No cities yet. Add cities to organize days and options.',
  removeCity: 'Remove',
  optionsFor: (cityName: string) => `Options — ${cityName}`,
  noOptions: 'No options yet. Add ideas to visit or do in this city.',
  addOption: 'Add option',
  optionTitle: 'Title',
  optionDescription: 'Description (optional)',
  optionUrl: 'URL (optional)',
  editOption: 'Edit',
  deleteOption: 'Delete',
  deleteOptionConfirm: 'Delete this option?',
  markComplete: 'Mark complete',
  markIncomplete: 'Mark incomplete',
  needDatesForPlan: 'Set start and end dates before planning the itinerary.',
  tripHeader: (title: string, dateLabel: string | null) =>
    dateLabel ? `${title} — ${dateLabel}` : title,
  tripTypeOptions: {
    organized_tour: 'Organized tour',
    cruise: 'Cruise',
    business: 'Business',
    backpacking: 'Backpacking',
    luxury: 'Luxury',
    adventure: 'Adventure',
    cultural: 'Cultural',
    other: 'Other',
  } as Record<string, string>,
} as const;

export const addTripEn = {
  pageTitle: 'Add',
  back: 'Back to My Trips',
  importHeading: 'Cruise Itinerary Import',
  manualEntry: 'Manual Entry',
  backToImport: 'Import cruise',
  title: 'Trip title',
  titleRequired: 'Trip title is required',
  tripType: 'Trip type',
  startDate: 'Start date',
  endDate: 'End date',
  save: 'Create trip',
  cancel: 'Cancel',
  importAction: 'Import',
  findCruiseLine: 'Find your cruise line',
  cruiseLineSelected: 'Cruise Line:',
  subBrand: 'Sub-brand',
  allSubBrands: 'All sub-brands',
  dateRange: 'Date',
  allDates: 'All dates',
  sailings: 'Sailings',
  sailingsCount: (n: number) => `${n} sailings`,
  travelDates: 'Pre and Post',
  preCruiseTitle: 'Date for Pre-Cruise',
  preCruiseArrival: 'Arrival',
  postCruiseTitle: 'Date for Post-Cruise',
  postCruiseDeparture: 'Departure',
  selectSailingFirst: 'Select a sailing first.',
  tripTitle: 'Trip Title',
  ports: 'Ports',
  portsCount: (n: number) => `${n} ports`,
  noPorts: 'This cruise has no ports to import.',
  portTimeRange: (arrival: string, departure: string) => `${arrival} - ${departure}`,
  portArrivalOnly: (time: string) => `Arr: ${time}`,
  portDepartureOnly: (time: string) => `Dep: ${time}`,
  tripDateRange: (start: string, end: string) => `Trip: ${start} – ${end}`,
  loadingCruiseLines: 'Loading cruise lines…',
  loadingSailings: 'Loading sailings…',
  loadingPorts: 'Loading ports…',
  noCruiseLines: 'No cruise lines available.',
  noSailings: 'No sailings found for this selection.',
  selectDateFirst: 'Select a date range to load sailings.',
  shipFilters: 'Ships',
  clearFilters: 'Clear',
  sailingRoundTrip: (port: string) => `${port} round-trip`,
  sailingPortToPort: (from: string, to: string) => `${from} to ${to}`,
  importing: 'Importing…',
  tripCreated: 'Trip created',
  importError: 'Import Error',
  duplicateTitle: 'Trip Already Exists',
  duplicateMessage: (names: string) =>
    `A trip matching this sailing already exists: ${names}. Opening the existing trip avoids creating a duplicate on all your devices.`,
  openExisting: 'Open Existing Trip',
  replaceExisting: 'Replace Existing Trip',
  addAsNew: 'Add as New Trip',
  addAsNewConfirmTitle: 'Create a duplicate trip?',
  addAsNewConfirmMessage:
    'This creates a second trip in Device Sync that will appear on all your devices (including iOS). Prefer Open Existing unless you intentionally need a separate copy.',
  addAsNewConfirmOk: 'Create Duplicate',
  syncingTrips: 'Syncing your trips…',
  ok: 'OK',
  editTripName: 'Trip name',
  enterTripName: 'Enter trip name',
  editBeforeSaving: 'Edit Before Saving',
} as const;

/** Create / Edit Trip page — mirrors iOS AddTripView / EditTripView */
export const tripEditEn = {
  newTripTitle: 'New Trip',
  editTripTitle: 'Edit Trip',
  createTrip: 'Create Trip',
  saveChanges: 'Save Changes',
  cancel: 'Cancel',
  deleteTrip: 'Delete Trip',
  deleteConfirm: 'Delete this trip? This cannot be undone.',
  deleteConfirmOk: 'Delete',
  sectionTripDetails: 'Trip Details',
  sectionTripInformation: 'Trip Information',
  sectionCities: 'Cities',
  tripName: 'Trip Name',
  tripNameRequired: 'Trip name is required',
  tripNamePlaceholder: 'Enter trip name',
  numberOfTravelers: 'Number of Travelers',
  temperatureUnit: 'Temperature unit',
  temperatureUnitF: '°F (Fahrenheit)',
  temperatureUnitC: '°C (Celsius)',
  tripType: 'Trip Type',
  description: 'Description',
  descriptionPlaceholder: 'Optional notes about this trip',
  addCity: 'Add City',
  addPort: 'Add Port',
  addSeaDay: 'Add Sea Day',
  cityNamePlaceholder: 'City name',
  portNamePlaceholder: 'Port name',
  arrivalDate: 'Arrival Date',
  departureDate: 'Departure Date',
  arrivalTime: 'Arrival Time',
  departureTime: 'Departure Time',
  seaDayDate: 'Date',
  embarkation: 'Embarkation',
  debarkation: 'Debarkation',
  portStatus: 'Port Status',
  dockNumber: 'Dock number',
  dockNumberPlaceholder: 'Dock / berth',
  removeCity: 'Remove',
  removeCityConfirm: 'Remove this city from the trip?',
  noCities: 'No cities yet. Add cities or ports below.',
  validationDates: 'Each city departure must be on or after arrival.',
  validationOverlap: 'City dates overlap. Adjust arrival and departure times.',
  saved: 'Trip saved',
  created: 'Trip created',
  deleted: 'Trip deleted',
  notFound: 'Trip not found',
  portStatusOptions: {
    docked: 'Docked',
    tendered: 'Tendered',
    cruising: 'Cruising',
    unknown: 'Unknown',
  } as Record<string, string>,
} as const;

/** Shared Notes / Attachments card — mirrors iOS NotesAttachmentsCardView */
export const notesAttachmentsEn = {
  title: 'Notes / Attachments',
  notesSection: 'Notes',
  attachmentsSection: 'Attachments',
  scopeToday: 'Today',
  scopeCity: 'City',
  scopeTrip: 'Trip',
  allTrip: 'All Trip',
  cityFilterLabel: 'Filter by city',
  addNote: 'Add Note',
  addFile: 'Add File',
  addPhoto: 'Add Photo',
  openInbox: 'Open inbound email inbox',
  pendingInbox: (n: number) => (n === 1 ? '1 pending confirmation' : `${n} pending confirmations`),
  viewAll: (n: number) => `View all ${n} attachments`,
  emptyToday: 'No notes or attachments for today.',
  emptyCity: 'No notes or attachments for this city.',
  emptyTrip: 'No notes or attachments for this trip.',
  emptyAll: 'No notes or attachments yet.',
  delete: 'Delete',
  deleteNote: 'Delete Note',
  open: 'Open',
  saveNote: 'Save',
  cancel: 'Cancel',
  noteTitleLabel: 'Title',
  noteContentLabel: 'Content',
  noteTitlePlaceholder: 'Title',
  noteContentPlaceholder: 'Content',
  notePlaceholder: 'Write a note for this day…',
  addNoteTitle: 'Add Note',
  editNoteTitle: 'Edit Note',
  uploadFailed: 'Failed to upload file.',
  downloadFailed: 'Failed to download file.',
  noAttachments: 'No attachments',
  processing: 'Uploading…',
  titleRequired: 'Title is required.',
} as const;

/** Inbound email confirmations inbox — mirrors iOS InboundConfirmationsControl */
export const inboundConfirmationsEn = {
  title: 'Inbound confirmations',
  pending: (n: number) => (n === 1 ? '1 pending' : `${n} pending`),
  inboundAddress: 'Your inbound address',
  copyAddress: 'Copy address',
  addressCopied: 'Address copied',
  addressUnavailable: 'Inbound email is not available yet for this account.',
  empty: 'No confirmations yet.',
  emptyWithAddress: (address: string) =>
    `No confirmations yet. Forward booking emails to ${address}.`,
  loadFailed: 'Could not load confirmations.',
  notProvisioned: 'Inbound email is not provisioned on this server yet.',
  from: 'From',
  received: 'Received',
  statusReceived: 'Received',
  statusCompleted: 'Completed',
  statusImported: 'Imported',
  attachments: (n: number) => (n === 1 ? '1 attachment' : `${n} attachments`),
  subjectFallback: 'Confirmation',
  markComplete: 'Mark complete',
  markCompleteFailed: 'Could not mark confirmation complete.',
  close: 'Close',
  backToList: 'Back',
  detailTitle: 'Confirmation',
  noBody: 'No preview available.',
  refresh: 'Refresh',
} as const;

/** Day View — mirrors iOS DayView */
export const dayViewEn = {
  back: 'Back to trip',
  untitledDay: 'Day',
  unknownCity: 'Unknown city',
  notFound: 'Day not found.',
  needDate: 'Set trip dates before adding items.',
  activities: 'Activities',
  addMealsActivities: 'Add meals, activities, and more',
  options: 'Things I May Do',
  noOptions: 'No options yet for this city.',
  notes: 'Notes & Attachments',
  notesEmpty: 'No notes for this day yet.',
  barcode: 'Code:',
  addItem: 'Add Item',
  expand: 'Expand',
  collapse: 'Collapse',
  hotel: 'Hotel',
  checkIn: 'Check-in',
  checkOut: 'Check-out',
  tonight: "Tonight's stay",
  confirmation: 'Confirmation',
  arrival: 'Arrival',
  departure: 'Departure',
  duration: 'Duration',
  minutes: 'min',
  noPortTimes: 'No port times for this day.',
  countryInfo: 'Country information',
  unknown: 'Unknown',
  address: 'Address',
  location: 'Location',
  phone: 'Phone',
  website: 'Website',
  openMap: 'Open in Maps',
  conf: 'Conf:',
  stayNights: 'nights',
  typeActivity: 'Activity',
  typeMeal: 'Meal',
  typeTransport: 'Transport',
  saveItem: 'Save',
  cancel: 'Cancel',
  titleLabel: 'Title',
  timeLabel: 'Time',
  notesLabel: 'Notes',
  attachmentsLabel: 'Attachments',
  noAttachments: 'No attachments',
  addAttachmentFile: 'Add File',
  addAttachmentPhoto: 'Add Photo',
  removeAttachment: 'Remove',
  attachmentUploadFailed: 'Failed to upload file.',
  attachmentDownloadFailed: 'Failed to download file.',
  confirmationLabel: 'Confirmation',
  durationLabel: 'Duration (min)',
  titleRequired: 'Enter a title',
  itemSaved: 'Item added',
  itemUpdated: 'Item updated',
  itemDeleted: 'Item deleted',
  editItem: 'Edit Item',
  deleteItem: 'Delete',
  deleteItemConfirm: 'Delete this item from the day?',
  typeLabel: 'Type',
} as const;

/** Place search sheet — mirrors iOS PlaceSearchView */
export const placeSearchEn = {
  title: 'Search Places',
  titleTours: 'Add Tour',
  titleManual: 'Add Manually',
  modePlaces: 'Places',
  modeTours: 'Tours',
  modeManual: 'Manual',
  modeLabel: 'Mode',
  searchPlaceholder: 'Search restaurants, sights, and more',
  enterManually: 'Enter Manually',
  manualHeading: 'Add Manually',
  manualBody: 'Enter meal, activity, or transport details for {city} without searching.',
  manualContinue: 'Continue',
  searching: 'Searching…',
  noResults: 'No places found. Try another search.',
  prompt: 'Search for a place to add to this day.',
  missingApiKey: 'Google Maps is not configured. You can still enter an item manually.',
  searchError: 'Place search failed. Try again or enter manually.',
  detailsError: 'Could not load place details.',
  typeMeal: 'Meal',
  typeActivity: 'Activity',
  typeTransport: 'Transport',
} as const;

/** Tours mode in Day View Add Item — mirrors iOS PlaceSearchView Tours */
export const tourSearchEn = {
  loading: 'Loading tours…',
  loadErrorTitle: "Couldn't load tours",
  emptyTitle: 'No tours available',
  emptyOffline: 'Connect to the internet to load tours for this city.',
  emptyForCity: 'No tours found for {city}.',
  countAvailable: '{count} tours available',
  retry: 'Try again',
  noCity: 'No city selected for tours.',
  saveFailed: 'Failed to save tour to plan.',
  pickerTitle: 'Add Tour',
  pickerDay: 'Day',
  pickerTime: 'Time',
  slotMorning: 'Morning',
  slotAfternoon: 'Afternoon',
  slotEvening: 'Evening',
  slotNight: 'Night',
  addTour: 'Add Tour',
  cancel: 'Cancel',
  durationHours: '{hours}h',
} as const;
